import { useCallback, useState } from 'react';

import Chip from '@pinvest/sirius-chip/dist/chip';
import cx from 'clsx';

import styles from './styles.module.css';
import type { MultiChipSelectProps } from './types';

const convertInitialValue = (values: string | string[]): string[] => {
  if (typeof values === 'string') return [values];

  return values;
};

const MultiChipSelect = ({
  options,
  groupName,
  type,
  values = '',
  scrollable,
  onChange,
  disabled,
  testId
}: MultiChipSelectProps) => {
  const [selectedChips, setSelectedChips] = useState<string[]>(convertInitialValue(values));

  const isChipSelected = useCallback((optionValue: string): boolean => {
    if (typeof selectedChips === 'string') {
      return selectedChips === optionValue;
    }

    return selectedChips.includes(optionValue);
  }, [selectedChips]);

  const handleChange = useCallback((newValues: string | string[]) => {
    if (typeof onChange === 'function') {
      if (typeof values === 'string') {
        onChange(newValues[0]);
        return;
      }

      onChange(newValues);
    }
  }, [onChange, values]);

  const handleChipClick = useCallback((optionValue: string, checked: boolean) => {
    if (type === 'radio') {
      setSelectedChips([optionValue]);
      handleChange([optionValue]);
      return;
    }

    let newSelectedChips = [...selectedChips];
    if (checked) {
      newSelectedChips = [...selectedChips, optionValue];
    } else {
      newSelectedChips = [...selectedChips].filter(v => v !== optionValue);
    }

    setSelectedChips(newSelectedChips);
    handleChange(newSelectedChips);
  }, [selectedChips, type, handleChange]);

  return (
    <div className={cx(styles.chips, { [styles.chips_scrollable]: !!scrollable })}>
      {options.map((opt, index) => {
        const chipClickHandlerFn = () => (checked: boolean) => {
          handleChipClick(opt.value, checked);
        };

        return (
          <Chip
            key={opt.value}
            name={groupName}
            disabled={disabled}
            type={type}
            isSelected={isChipSelected(opt.value)}
            onChange={chipClickHandlerFn()}
            testId={`${testId}-${index + 1}`}
          >
            {opt.label}
          </Chip>
        );
      })}
    </div>
  );
};

export default MultiChipSelect;
